import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Input, Pagination, Table, Icon, Modal, Image } from 'react-frontier2';
import { CatalogEvent, EventDate, SetLoading } from '@arema/components/Classes';
import { CDN_URL, PLACEHOLDER_IMG } from '../AdminConfig';
import API from '../API';
import classNames from 'classnames';
import { fromUnix } from '@arema/components/Util';

interface SearchModalProps{
	open: boolean,
	onClose: ()=>void,
	title?: string,
	onEventSelected?: (event: CatalogEvent)=>boolean;
	onDateSelected?: (event: CatalogEvent, date: EventDate)=>boolean;
	pageSize?: number,
	date?: boolean,
}

var SearchEventModal = (props: SearchModalProps)=>{
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [searchResults, setSearchResults] = useState<CatalogEvent[]>(null);
	var [searchError, setSearchError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var [dates, setDates] = useState<EventDate[]>(null);
	var [selectedEvent, setSelectedEvent] = useState<CatalogEvent>(null);
	var inputRef = useRef<HTMLInputElement>(null);
	const PAGE_SIZE = (props.pageSize || 15);

	useEffect(()=>{
		if(props.open){
			setSearchInput('');
			inputRef.current?.focus();
		}
	}, [props.open]);

	var searchEvent = (setLoading: SetLoading, page?: number)=>{
		if(searching) return;
		setSearching(true);
		setSentQuery(searchInput);
		setPage(page || 0);
		setSearchError(null);
		API.searchEvent(searchInput, page*PAGE_SIZE, PAGE_SIZE).then(res=>{
			if(res.error) return setSearchError(res.message);
			setSearchResults((res.data as any[]) as CatalogEvent[]);
		}).catch(err=>{
			setSearchError('Hubo un error inesperado buscando los eventos (LCL-1)');
		}).finally(()=>{
			setSearching(false);
		});
	}

	var viewCalendarios = (event_id: number)=>{
		setSearching(true);
		API.getEventDates(event_id).then(res=>{
			if(res.error) return setSearchError(res.message);
			setDates(res.data);
		}).catch(err=>{
			setSearchError('Hubo un error buscando los calendarios de el evento.');
		}).finally(()=>{
			setSearching(false);
		})
	}

	var eventSelected = (event: CatalogEvent)=>{
		return ()=>{
			if(props.date){
				viewCalendarios(event.event_id);
				setSelectedEvent(event);
			}else{
				if(!props.onEventSelected) return;
				var done = props.onEventSelected(event);
				if(done!==false){
					props.onClose();
				}
			}
		}
	}

	var dateSelected = (date_id: number)=>{
		if(!props.onDateSelected) return;
		var date = dates.find(a=>a.date_id===date_id);
		if(!date) return;
		var done = props.onDateSelected(selectedEvent, date);
		if(done!==false){
			props.onClose();
		}
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		searchEvent(null, page);
	}
	
	var hasPagination = (searchResults?.length || 0)>=PAGE_SIZE || page!=0;
	
	return <Modal open={props.open} onClose={props.onClose} size={'small'}>
		<Modal.Header>{props.title || (!selectedEvent ? 'Buscar evento' : 'Seleccionar calendario')}</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20 }}>
			{!selectedEvent ? (
				<Input ref={inputRef} value={searchInput} onChange={setSearchInput} placeholder='Buscar registros' buttons={(
					<Button text='Buscar' onClick={searchEvent} loading={searching} />
				)} onSubmit={searchEvent} iconName='search' />
			) : (
				<Input value={selectedEvent.event_name} iconName='calendar' readonly buttons={(
					<Button text='Regresar' onClick={()=>setSelectedEvent(null)} color='black' />
				)} />
			)}
			{searching ? (
				<Header loading text={`Cargando ${selectedEvent ? 'calendarios' : 'eventos'}`} style={{ margin: 0, padding: '50px 0' }} />
			) : searchError ? (
				<Header text='Error' subtext={searchError} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : !searchResults ? (
				null
			) : searchResults.length===0 ? (
				<Header text='Eventos no encontrados' subtext={`No se encontraron eventos con "${sentQuery}"`} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : !selectedEvent ? (
				<Table selectable fitted style={{ marginBottom: -20, borderBottomRightRadius: 12, borderBottomLeftRadius: 12 }}>
					<Table.Head data={[[<Icon name='image' />, 'ID', 'Evento', 'Fecha']]} />
					<Table.Body>
						{searchResults.map(a=>(
							<Table.Row
								onClick={eventSelected(a)}
								collapsingIndexes={[0, 1, 3]}
								data={[
									<Image src={`${CDN_URL}/events/${a.event_id}/200.webp`} style={{ width: 40, height: 40, borderRadius: 4, marginBottom: -5 }} fallback={PLACEHOLDER_IMG} />,
									a.event_id,
									a.event_name,
									a.date ? (
										<>
											<div style={{ fontSize: 12, color: 'gray' }}>Próxima fecha</div>
											{fromUnix(a.date).toFormat('dd/MMM/yyyy')}
										</>
									) : (
										<Icon color='grey' name='minus' />
									)
								]} 
							/>
						))}
					</Table.Body>
					<Table.Footer actions={(
						<Pagination inline page={page} disabled={searching} onPageChange={onPageChange} hasNext={searchResults.length>=PAGE_SIZE} />
					)} />
				</Table>
			) : (
				<Table fitted selectable divided style={{ marginTop: 20 }} className='first bottom round'>
					<Table.Head
						data={[['ID', 'Fecha', 'Recinto', 'Ciudad', <Icon name='eye' />]]}
						collapsingIndexes={[0, 2, 3, 4]}
					/>
					<Table.Body>
						{dates.length===0 && (
							<Table.Cell row empty colSpan={100} value={'No se encontraron fechas para este evento'} />
						)}
						{dates.sort((a,b)=>a.date-b.date).map(a=>(
							<Table.Row 
								onClick={()=>dateSelected(a.date_id)} 
								collapsingIndexes={[0, 2, 3, 4]}
								data={[
									a.date_id, 
									fromUnix(a.date).toFormat('dd/MMM/yy HH:mm'), 
									a.venue_name, 
									a.city,
									<Icon name={classNames({
										remove: !a.available_web && !a.available_pdv,
										check: a.available_web || a.available_pdv,
									}) as any} color={classNames({
										grey: !a.available_web && !a.available_pdv,
										green: a.available_web && a.available_pdv,
										orange: (!a.available_web || !a.available_pdv) && !(!a.available_web && !a.available_pdv),	
									})} />
									// <i className={classNames('icon', {
									// 	'remove grey': !a.available_web && !a.available_pdv,
									// 	check: a.available_web || a.available_pdv,
									// 	green: a.available_web && a.available_pdv,
									// 	orange: (!a.available_web || !a.available_pdv) && !(!a.available_web && !a.available_pdv),
									// })}></i>
								]} 
							/>
						))}
					</Table.Body>
				</Table>
			)}
		</Modal.Content>
	</Modal>
}

export default SearchEventModal;